import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import Table from '../../../../component/table/Table';
import OdKpiRow from '../components/OdKpiRow';
import odTableStyles from '../components/odTableStyles';
import th from '../components/odColumnHeader';
import {
    buildOdPairs,
    compactDuration,
    hasEnergyData,
    hasFuelData,
    UNKNOWN_PLACE,
} from '../odAnalyticsUtils';

const TOP_N = 10;

const num = (value) => <span className="od-cell-num">{value}</span>;

/** Truncate a long place name for the chart axis, which has no room to wrap. */
const shortLabel = (pair) => {
    const clip = (text) => (text.length > 18 ? `${text.slice(0, 17)}…` : text);
    return `${clip(pair.origin)} → ${clip(pair.destination)}`;
};

/**
 * Origin → destination patterns. The tab Route Atlas does not have, and the one that
 * earns this panel its name.
 */
const OdPairsView = (props) => {
    const {
        trips, fuelTrips, pairFilter, onSelectPair,
    } = props;

    // See OverviewView: without the ledger rows a CAN fleet's Fuel column reads 0 L, or vanishes.
    const pairs = useMemo(() => buildOdPairs(trips, fuelTrips), [trips, fuelTrips]);
    const showFuel = useMemo(() => hasFuelData(trips, fuelTrips), [trips, fuelTrips]);
    const showEnergy = useMemo(() => hasEnergyData(trips), [trips]);

    const top = useMemo(() => pairs.slice(0, TOP_N), [pairs]);
    const unknownPairs = useMemo(
        () => pairs.filter((p) => p.origin === UNKNOWN_PLACE || p.destination === UNKNOWN_PLACE).length,
        [pairs],
    );

    const busiest = pairs[0];
    const repeatPairs = pairs.filter((pair) => pair.trips > 1).length;
    const oneOffPairs = pairs.length - repeatPairs;

    const tiles = [
        { key: 'pairs', label: 'Distinct O/D pairs', value: pairs.length },
        {
            key: 'repeat',
            label: 'Repeated pairs',
            value: repeatPairs,
            sub: `${oneOffPairs} travelled once`,
        },
        busiest && {
            key: 'busiest',
            label: 'Busiest pair',
            value: busiest.trips,
            unit: busiest.trips === 1 ? 'trip' : 'trips',
            sub: busiest.pairLabel,
        },
        unknownPairs > 0 && {
            key: 'unknown',
            label: 'Pairs with an unnamed end',
            value: unknownPairs,
            sub: 'no reverse-geocoded place on one side',
            tone: 'warn',
        },
    ];

    const chartOptions = useMemo(() => ({
        chart: {
            id: 'od-pairs-chart',
            type: 'bar',
            toolbar: { show: false },
            fontFamily: 'Inter',
            animations: { enabled: false },
            events: {
                dataPointSelection: (_event, _ctx, config) => {
                    const pair = top[config.dataPointIndex];
                    if (pair) onSelectPair({ origin: pair.origin, destination: pair.destination });
                },
            },
        },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, borderRadiusApplication: 'end' } },
        colors: ['#365A6D'],
        dataLabels: {
            enabled: true,
            style: { fontSize: '12px', fontFamily: 'Inter', colors: ['#FFFFFF'] },
            offsetX: -8,
        },
        grid: { borderColor: '#EAECF0', strokeDashArray: 4 },
        xaxis: {
            categories: top.map(shortLabel),
            title: { text: 'Trips', style: { color: '#667085', fontSize: '12px', fontWeight: 500 } },
            labels: { style: { colors: '#667085', fontSize: '12px' } },
        },
        yaxis: { labels: { style: { colors: '#475467', fontSize: '12px' }, maxWidth: 260 } },
        legend: { show: false },
        states: { active: { filter: { type: 'darken', value: 0.85 } } },
        tooltip: {
            // The axis label is truncated; the tooltip shows the full pair.
            y: {
                formatter: (value, { dataPointIndex }) => {
                    const pair = top[dataPointIndex];
                    return pair ? `${value} trips · ${pair.distanceKm.toLocaleString()} km` : `${value} trips`;
                },
                title: { formatter: (_seriesName, { dataPointIndex }) => top[dataPointIndex]?.pairLabel || '' },
            },
        },
        noData: { text: 'No trips in this window', style: { color: '#667085', fontFamily: 'Inter' } },
    }), [top, onSelectPair]);

    // Place names take the slack via `grow`, but their minimum has to stay small or the
    // eight columns cannot fit the card. They wrap rather than ellipsis, so a narrow column
    // costs a taller row instead of hiding the name. Numeric widths are the header label
    // plus 20px padding and the 18px RDT reserves for the sort arrow.
    const columns = useMemo(() => [
        {
            name: th('Origin'),
            selector: (row) => row.origin,
            sortable: true,
            grow: 2,
            minWidth: '8rem',
            cell: (row) => <span className="od-cell-strong" title={row.origin}>{row.origin}</span>,
        },
        {
            name: th('Destination'),
            selector: (row) => row.destination,
            sortable: true,
            grow: 2,
            minWidth: '8rem',
            cell: (row) => <span className="od-cell-strong" title={row.destination}>{row.destination}</span>,
        },
        {
            name: th('Trips'), selector: (row) => row.trips, sortable: true, right: true, minWidth: '4.5rem', cell: (row) => num(row.trips),
        },
        // Abbreviated like Route Atlas's headers: "Total distance" and "Avg distance" were
        // both wider than the values beneath them.
        {
            name: th('Total dist'), selector: (row) => row.distanceKm, sortable: true, right: true, minWidth: '6.5rem', cell: (row) => num(`${row.distanceKm.toLocaleString()} km`),
        },
        {
            name: th('Avg dist'), selector: (row) => row.avgDistanceKm, sortable: true, right: true, minWidth: '5.75rem', cell: (row) => num(`${row.avgDistanceKm.toLocaleString()} km`),
        },
        {
            name: th('Avg dur'), selector: (row) => row.avgDurationSec, sortable: true, right: true, minWidth: '5.25rem', cell: (row) => num(compactDuration(row.avgDurationSec)),
        },
        showFuel && {
            name: th('Fuel'), selector: (row) => row.fuelLitre, sortable: true, right: true, minWidth: '4.5rem', cell: (row) => num(`${row.fuelLitre.toLocaleString()} L`),
        },
        showEnergy && {
            name: th('Energy'), selector: (row) => row.energyKwh, sortable: true, right: true, minWidth: '5.5rem', cell: (row) => num(`${row.energyKwh.toLocaleString()} kWh`),
        },
    ].filter(Boolean), [showFuel, showEnergy]);

    const activeLabel = pairFilter ? `${pairFilter.origin} → ${pairFilter.destination}` : null;

    const rowStyles = useMemo(() => [{
        when: (row) => row.pairLabel === activeLabel,
        style: { backgroundColor: '#F9FAFB', boxShadow: 'inset 0.1875rem 0 0 #365A6D' },
    }], [activeLabel]);

    return (
        <div className="od-view">
            <OdKpiRow tiles={tiles} />

            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">{`Top ${Math.min(TOP_N, top.length)} origin → destination pairs`}</div>
                        <div className="od-card-subtitle">Click a bar to filter the Route Map to that pair</div>
                    </div>
                </div>
                <div className="od-card-body od-chart od-chart-tall">
                    <ReactApexChart
                      options={chartOptions}
                      series={[{ name: 'Trips', data: top.map((pair) => pair.trips) }]}
                      type="bar"
                      height={Math.max(240, top.length * 34 + 80)}
                    />
                </div>
            </div>

            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">All pairs</div>
                        <div className="od-card-subtitle">
                            Click a row to filter the Route Map to that pair
                        </div>
                    </div>
                </div>
                <div className="od-card-body od-table-wrap">
                    <Table
                      tableData={pairs}
                      columns={columns}
                      module="odAnalytics"
                      fixedHeaderScrollHeight="32rem"
                      handlePageChange={() => {}}
                      handlePerRowsChange={() => {}}
                      setSelectedRowCount={() => {}}
                      totalRows={pairs.length}
                      customStyles={odTableStyles}
                      rowStyles={rowStyles}
                      onRowClicked={(row) => onSelectPair(
                          row.pairLabel === activeLabel
                              ? null
                              : { origin: row.origin, destination: row.destination },
                      )}
                      highlightOnHover
                      pointerOnHover
                    />
                </div>
                <div className="od-card-note">
                    Pairs are matched on the exact place name the API returns, and direction matters —
                    A → B and B → A are separate rows. Grouping by city or suburb needs the structured
                    origin and destination fields, which the trip endpoint currently flattens into a
                    single string before returning them.
                </div>
            </div>
        </div>
    );
};

export default OdPairsView;
